import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';
import styled from 'styled-components';
import queryString from 'query-string';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './lead-form-cx.scss';

const defaultFormValues = {
  name: '',
  phone: '',
  zip: '',
  numberOfPowerwalls: 0,
  hasPermanentBackupGenerator: undefined,
  hasSmartMeter: undefined,
};

const LeadFormCX = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const mode = props.blok.mode || '';

  const onSubmit = async (values) => {
    setSubmitState('');

    if (!values.email && !values.phone) {
      setSubmitState('failed');
      setErrorMessage('Please provide either your email address or phone number (or both).');
      return;
    }

    try {
      const leadSource = props.blok.lead_source || 'Affiliate';
      const utmContent = props.blok.utm_content || 'EnrollCX';
      const leadSourceMedium = props.blok.lead_source_medium || 'CX';

      const parsed = queryString.parse(window.location.search);
      let owner = '';
      let leadSourceAccount = '';

      if (parsed && parsed.partnerId && props.blok.owner_from_partnerid) {
        owner = parsed.partnerId;
      }

      if (parsed && parsed.partnerId && props.blok.lead_source_account_from_partnerid) {
        leadSourceAccount = parsed.partnerId;
      }

      if (!leadSourceAccount) {
        const isProd =
          window.location.origin.indexOf('swellenergy.com') > -1 &&
          window.location.origin.indexOf('sbx') === -1;
        const defaultLeadSourceAccounts = props.blok.lead_source_account
          ? JSON.parse(props.blok.lead_source_account)
          : { dev: '0016100000fehkAAAQ', prod: '0014M00001k8A7fQAE' };
        leadSourceAccount = isProd ? defaultLeadSourceAccounts.prod : defaultLeadSourceAccounts.dev;
      }

      const notes =
        `How many Powerwalls do you have, or plan to have, installed? ${values.numberOfPowerwalls}` +
        '\r\n' +
        `Do you have a permanent backup generator installed at your home? ${
          values.hasPermanentBackupGenerator ? 'yes' : 'no'
        }`;

      await leads.submitLead({
        ...values,
        leadSource,
        leadSourceAccount,
        leadSourceMedium,
        utmContent,
        owner,
        customerType: 'cx',
        notes,
      });

      setSubmitState('success');
      metrics.trackLeadFormSubmit(window.location.origin + window.location.pathname, 'LeadFormCX');
    } catch (e) {
      Sentry.captureException(e, {
        lead: values,
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['lfcx'];
  containerClasses.push('theme-' + props.blok.theme);
  containerClasses.push('accent-' + props.blok.accent_color);

  const title = props.blok.title || '';
  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const successMessage =
    props.blok.success_message ||
    'Thank you for submitting your information. A Swell Energy representative will contact you shortly to discuss your options.';

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {submitState !== 'success' && (
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
              name: Yup.string().required('Required'),
              phone: Yup.string().matches(PhoneRegex, 'Enter your phone number'),
              email: Yup.string().email('Enter a valid e-mail address'),
              zip: Yup.string()
                .required('Please enter your zip code')
                .matches(/\d{5}/, 'Enter your ZIP code'),
            })}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

              return (
                <Form className="input-form">
                  {title && <h2 className={`theme-${props.blok.theme}`}>{title}</h2>}
                  <TrackFormDirty
                    formComponent="LeadFormCX"
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
                  <div className="field">
                    <Field name="name" placeholder="Name" />
                    <ErrorMessage className="error" component="div" name="name" />
                  </div>
                  <div className="field">
                    <Field name="phone" placeholder="Phone">
                      {({ field }) => (
                        <InputMask
                          {...field}
                          mask="(999) 999-9999"
                          placeholder="Phone"
                          type="text"
                          alwaysShowMask={false}
                        />
                      )}
                    </Field>
                    <ErrorMessage className="error" component="div" name="phone" />
                  </div>
                  <div className="field">
                    <Field name="email" placeholder="Email" />
                    <ErrorMessage className="error" component="div" name="email" />
                  </div>
                  <div className="field">
                    <Field name="zip" placeholder="Zip Code">
                      {({ field }) => (
                        <InputMask
                          {...field}
                          mask="99999"
                          placeholder="Zip Code"
                          type="text"
                          alwaysShowMask={false}
                        />
                      )}
                    </Field>
                    <ErrorMessage className="error" component="div" name="zip" />
                  </div>
                  <div className="field">
                    <Field name="hasSmartMeter" type="checkbox" id="hasSmartMeter" />
                    <label htmlFor="hasSmartMeter">
                      {props.blok.smart_meter_prompt || 'I have an SCE Smart Meter'}
                    </label>
                  </div>
                  {!props.blok.hide_powerwall_option && (
                    <div className="field">
                      <span className="cx-dd-header">
                        How many Powerwalls do you have, or plan to have, installed?
                      </span>
                      <Field name="numberOfPowerwalls" as="select" placeholder="(select)">
                        <option value="">(select)</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4+">4 or more</option>
                      </Field>
                    </div>
                  )}
                  {!props.blok.hide_generator_option && (
                    <div className="field">
                      <span className="cx-dd-header">
                        Do you have a permanent backup generator installed at your home?
                      </span>
                      <Field name="hasPermanentBackupGenerator" as="select" placeholder="(select)">
                        <option value="">(select)</option>
                        <option value={true}>Yes</option>
                        <option value={false}>No</option>
                      </Field>
                    </div>
                  )}
                  {submitState === 'failed' && <div className="error">{errorMessage}</div>}
                  <button disabled={formik.isSubmitting} type="submit">
                    {buttonText}
                  </button>
                  <Disclaimer>By submitting this form, I agree to the{' '}
                      <a href="/privacy-policy">Privacy Policy</a> and consent to be contacted by or
                      on behalf of Swell Energy, including by email, calls, and text messages to any
                      telephone number that I provide, about my interest in Swell Energy products
                      and services. I understand my consent is not required to purchase.</Disclaimer>
                  
                </Form>
              );
            }}
          </Formik>
        )}
        {submitState === 'success' && <div className="success">{successMessage}</div>}
      </div>
    </SbEditable>
  );
};

const Disclaimer = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  display: block;
`;

export default LeadFormCX;
